import { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backend";

// T12.b runtime branding: CSS variables + favicon from /api/branding (cached, ETag). The inline script in index.html
// applies the last-known variables before first paint; SSR inlines the current ones.
const LS = "rhyte.brand.v1";
let current = null;
const subs = new Set();
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export function applyBrand(b) {
  if (!b) return;
  const root = document.documentElement.style;
  Object.entries(b.vars || {}).forEach(([k, v]) => root.setProperty(`--${k}`, v));
  const icon = (rel, sizes, href) => {
    if (!href) return;
    let l = document.querySelector(`link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ""}`);
    if (!l) { l = document.createElement("link"); l.rel = rel; if (sizes) l.sizes = sizes; document.head.appendChild(l); }
    l.href = abs(href);
  };
  if (b.icons) { icon("icon", "any", b.icons.ico); icon("icon", "32x32", b.icons.favicon_32); icon("icon", "16x16", b.icons.favicon_16); icon("apple-touch-icon", "180x180", b.icons.apple); }
  current = b;
  subs.forEach((f) => f(b));
}

let inflight = null;  // perf: concurrent first-paint callers share one request (not a cache: cleared when it settles)
export function loadBrand(force) {
  if (current && !force) return Promise.resolve(current);
  if (inflight && !force) return inflight;
  inflight = fetch(`${BACKEND_URL}/api/branding`, { cache: force ? "no-cache" : "default" })
    .then((r) => r.json()).then((b) => { try { localStorage.setItem(LS, JSON.stringify({ vars: b.vars })); } catch (e) {} applyBrand(b); return b; })
    .catch(() => null).finally(() => { inflight = null; });
  return inflight;
}

export function useBrand() {
  const [b, setB] = useState(current);
  useEffect(() => { subs.add(setB); loadBrand(); return () => subs.delete(setB); }, []);
  return b;
}

export const brandAsset = abs;

// Logo with explicit size (no CLS). Uploaded logos are only drawn on white surfaces; onDark always uses the wordmark.
export function BrandLogo({ onDark = false, height = 32, maxWidth, testid = "brand-logo" }) {
  const b = useBrand();
  const [failed, setFailed] = useState("");  // T53: a custom logo that fails to load falls back to the wordmark (never alt text)
  const logo = b && b.logo && failed !== b.logo.png_1x ? b.logo : null;
  if (logo) {
    let w = Math.round((logo.w * height) / logo.h);
    const h = maxWidth && w > maxWidth ? Math.round((height * maxWidth) / w) : height;  // T33a: fit wide logos
    if (h !== height) w = maxWidth;
    const img = (
      <picture data-testid={testid}>
        <source type="image/webp" srcSet={`${abs(logo.webp_1x)} 1x, ${abs(logo.webp_2x)} 2x`} />
        <img src={abs(logo.png_1x)} srcSet={`${abs(logo.png_1x)} 1x, ${abs(logo.png_2x)} 2x`} width={w} height={h} alt={b.alt || "Rhyte Step IT"} decoding="async" style={{ display: "block", width: w, height: h }}
          onError={() => setFailed(logo.png_1x)} data-testid={`${testid}-img`} />
      </picture>
    );
    if (!onDark) return img;
  }  // on dark surfaces an uploaded (white-background) logo would read as a white box: use the knock-out wordmark
  return (
    <span data-testid={testid} data-wordmark="true" role="img" aria-label="Rhyte Step IT" style={{ display: "inline-flex", alignItems: "baseline", gap: 6, fontWeight: 800, letterSpacing: "-0.01em", fontSize: Math.round(height * 0.62), lineHeight: `${height}px`, height, whiteSpace: "nowrap" }}>
      <span style={{ color: onDark ? "#fff" : "var(--brand-primary)" }}>RHYTE STEP</span>
      <span style={{ color: "var(--brand-accent)" }}>IT</span>
    </span>
  );
}

// T37: compact mark for the minimised admin rail: the uploaded site icon, else the first letters of the store name.
export function BrandMark({ size = 32, testid = "brand-mark" }) {
  const b = useBrand();
  const src = b?.icons?.apple || b?.icons?.favicon_32;
  if (src) return <img src={abs(src)} alt={b?.store_name || "Home"} width={size} height={size} style={{ objectFit: "contain", borderRadius: 6 }} data-testid={testid} />;
  const name = (b?.store_name || "RS").replace(/[^A-Za-z ]/g, "").split(/\s+/).filter(Boolean);
  const ini = (name.length > 1 ? name[0][0] + name[1][0] : (name[0] || "RS").slice(0, 2)).toUpperCase();
  return <span data-testid={testid} style={{ width: size, height: size, borderRadius: 8, background: "var(--brand-primary, #0B1F3A)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: size * 0.42 }}>{ini}</span>;
}
